import { Card, CardContent, CardHeader, Divider, Typography, ListItem, ListItemText, Pagination, useTheme, Link } from "@mui/material";
import { ChangeEvent, FC, useCallback, useEffect, useState } from "react";
import { AlertIcon, AlertList, displayAlert, NoAlerts } from "./alertCard";
import { useShowContext } from "ra-core";
import { RecPatch } from "@/dataProvider/tools";
import { AlertAndTime, BackupDashboardResponseDto } from "@/gen/api/backup";
import { chunk } from "lodash";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { Link as RouteLink } from 'react-router-dom'
import { genUrlWithHashid } from "@/utils/hashid";

const pageSize = 3;

const DashboardAlertsCard: FC = () => {
  const { record } = useShowContext<BackupDashboardResponseDto & RecPatch>()
  const alertCount = (record 
    ? (record.backupAlerts?.length || 0) + (record.validationAlerts?.length || 0) 
    : 0)

  const [page, setPage] = useState(1) // 1 based page location
  const [alertChunks, setAlertChunks] = useState<AlertAndTime[][]>([])
  useEffect(() => {
    setPage(1)
    const alertList = 
      record 
      ? [...(record.backupAlerts || []), ...(record.validationAlerts || [])]
        .sort((a, b) => (b.alertTime || '') > (a.alertTime || '') ? -1 : 1) 
      : []
    setAlertChunks(chunk(alertList, pageSize))
  }, [record, setPage, setAlertChunks])

  const handleChange = useCallback((e: ChangeEvent<any>, value: number) => {
    setPage(value)
  }, [setPage])

  const format = useUserTimeFormat()
  const theme = useTheme()
  return (
    <Card>
      <CardHeader
        title={(
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Alerts
          </Typography>
        )}
      />
      <CardContent sx={{ pt: 0 }} >
        <Divider />
        {alertCount > 0 ?
          <AlertList>
            {alertChunks[page - 1]?.map((a:AlertAndTime, i) => (
              <ListItem key={a.databaseId + '' + i} divider>
                <Link 
                  underline="none"
                  component={RouteLink}
                  to={genUrlWithHashid('/backups-n-validations/list/$0/show', a.databaseId || 0)}
                >
                  <AlertIcon {...a} />
                  <ListItemText
                    disableTypography
                    primary={(
                      <Typography
                        className={a.isWarning ? 'warning' : 'error'}
                        variant="body2"
                      >
                        {displayAlert(a)}
                      </Typography>
                    )}
                    secondary={(
                      <Typography
                        className={a.isWarning ? 'warning' : 'error'}
                        variant="caption"
                      >
                        {a.alertTime && format(a.alertTime, 'PPp')}
                      </Typography>
                    )}
                  />
                </Link>
              </ListItem>))
            }
          </AlertList> :
          <NoAlerts />
        }
        {alertChunks.length > 1 &&
          <Pagination 
            count={Math.ceil(alertCount / pageSize)} 
            page={page}
            defaultPage={1}
            sx={{ mb: -2 }} 
            onChange={handleChange} 
          />        
        }
      </CardContent>
    </Card>
  )
}

export default DashboardAlertsCard;

